/**
 * shared/acting-context.ts
 *
 * The acting-context wire contract (org/personal separation, #2459) — the one
 * place the server, the API client, and the shared query client agree on how a
 * "you are wearing the wrong hat" refusal is spelled.
 *
 * WHY SHARED RATHER THAN A SERVER CONSTANT. The code below is a value BOTH
 * halves must match on: the server writes it, `wrongActingContextOf` reads it to
 * decide whether to render the switch prompt, and `queryClient`'s retry policy
 * reads it to decide the failure is settled rather than transient. Three
 * independent string literals is the shape where one gets renamed and the other
 * two silently stop matching — the switch prompt would quietly degrade back into
 * the "could not load" dead end, with nothing failing to say so.
 */

/**
 * The read was refused because the resource belongs to a DIFFERENT acting
 * context of the caller's own — not because it is missing, and not because they
 * lack authority. Distinct from `NOT_FOUND`, which stays the existence-hiding
 * answer for a caller with no authority anywhere.
 */
export const WRONG_ACTING_CONTEXT = "WRONG_ACTING_CONTEXT" as const;

/** HTTP status paired with {@link WRONG_ACTING_CONTEXT}: authorised, wrong seat. */
export const WRONG_ACTING_CONTEXT_STATUS = 409;

/**
 * The seat that WOULD admit the caller, returned on the refusal so the client
 * can offer the switch instead of a dead end. Safe to disclose: the server emits
 * it only after confirming this caller may read the resource through that seat,
 * so it describes their own membership and nothing about the resource.
 *
 * `nodeKind` is carried so the client renders the platform's own word for the
 * personal seat ("Personal", per `resolveActiveProfile`) rather than the raw
 * node name, which is not a label shown anywhere else in the product.
 */
export interface WrongActingContext {
  nodeId: string;
  name: string;
  nodeKind: "personal" | "org";
}
